/* Copyright (c) 2021 Paul Nebel */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNull, isUndefined } from 'lodash';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfo, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { faArrowToBottom } from '@fortawesome/pro-solid-svg-icons';

import addToHomescreen from '../core/utils/add-to-homescreen';
import LogoSmall from './logo-small';

const LogoSmallIcon = styled(LogoSmall)` 
    width: 30px; 
    height: 30px;
`;

const closedModalClass = 'modal p-5';
const openModalClass = 'modal is-active is-clipped p-5';
let hasLocalStorage = false;

const InstallPwaNotification = (props = { options: {} }) => {
    const retrievePreviousPlatform = () => {
        let platform = {};
        if (hasLocalStorage) {
            const keys = Object.keys(localStorage);
            for(let key of keys) {
                if (key === 'platform') {
                    try {
                        platform = JSON.parse(localStorage.getItem(key));
                    } catch (ex) {
                        break;
                    }
                    break;
                }
            }
        }
        return platform;
    };
    const [session, setSession] = useState(retrievePreviousPlatform());
    const [modal, setModal] = useState({ state: 'info' });

    const updateSession = useCallback(
        (newSession) => {
            setSession(prevSession => {
                const combinedSession = { ...prevSession, ...newSession };
                if (hasLocalStorage) {
                    localStorage.setItem('platform', JSON.stringify(combinedSession));
                } else {
                    return {};
                }
                return combinedSession;
            });
        },
        [session],
    );

    useEffect(
        () => {
            const getPreviousPlatform = () => {
                let platform = {};
                if ('localStorage' in window) {
                    hasLocalStorage = true;
                    platform = retrievePreviousPlatform();        
                }
                return platform;
            };
            const previousPlatform = getPreviousPlatform();
            const a2hs = () => {
                const platform = addToHomescreen({
                    ...previousPlatform,
                    appId: 'chord-flow',
                    name: props.name, 
                    onCanInstall: (pForm) => {
                        updateSession(pForm);
                    },
                    onBeforeInstallPrompt: (pForm) => {
                        updateSession(pForm);
                    },
                });
                updateSession(platform);
            };
            a2hs();
        },
        [],
    );

    const options = {
        ...props.options,
    };

    // this will execute a custom criteria method or just check a value to see if it returns true
    // this gives the application developer a hook to add extra custom logic to the workflow
    const passCustomCriteria = () => {
        const customCriteria = options.customCriteria;
        if (!(isNull(customCriteria) || isUndefined(customCriteria))) {
            let passCustom = false;
            if (typeof customCriteria === 'function') {
                passCustom = customCriteria();
            } else {
                passCustom = !!customCriteria;
            }
            return passCustom;
        }
        console.log('no custom criteria, carry on');
        // no custom criteria so just return true to not block the process
        return true;
    };

    const getPlatform = (native) => {
        // these should represent just about all browsers that offer an A2HS experience
        if (session.isChromium && native === undefined && !native) {
            return 'native';
        } else if (session.isFireFox) {
            return 'firefox';
        } else if (session.isiPad) {
            if (session.isMobileSafari) {
                return 'ipadMobileSafari';
            } else {
                return 'ipadOther';
            }
        } else if (session.isiPhone) {
            if (session.isMobileSafari) {
                return 'iphoneMobileSafari';
            } else {
                return 'iphoneOther';
            }
        } else if (session.isOpera) {
            return 'opera';
        } else if (session.isSamsung) {
            return 'samsung';
        } else if (session.isEdge) {
            return 'edge';
        } else if (session.isChromium) {
            return 'chromium';
        } else {
            return '';
        }
    };
    
    const handleInstall = (evt) => {
        if (options.onInstall) {
            options.onInstall(evt);
        }
        const pForm = getPlatform();
        if (session.beforeInstallPrompt && (pForm === 'native')) {
            triggerNativePrompt();
        } else {
            if (/iphone|ipad/i.test(pForm)) {
                if (/MobileSafari/i.test(pForm)) {
                    setModal({ state: 'mobileSafari' });
                } else {
                    setModal({ state: 'iosOther' });
                }
            }
        }
        return false;
    };

    const handleCancel = (evt) => {
        if (options.onCancel) {
            options.onCancel(evt);
        }
        updateSession({ optedout: true });
        notificationRef.current.className = 'is-hidden';
        return false;
    };

    const handleInfo = () => {
        modalRef.current.className = openModalClass;
        setModal({ state: 'info' });
    };

    /* displays native A2HS prompt & stores results */
    const triggerNativePrompt = () => {
        if (!session.beforeInstallPrompt) {
            return Promise.resolve();
        }

        return session.beforeInstallPrompt
            .prompt()
            .then(() => {
                // Wait for the user to respond to the prompt
                return session.beforeInstallPrompt.userChoice;
            })
            .then((choiceResult) => {
                const newSession = {};
                newSession.added = choiceResult.outcome === 'accepted';

                if (newSession.added) {
                    console.log('User accepted the A2HS prompt');
                    if (options.onAdd) {
                        options.onAdd(choiceResult);
                    }
                } else {
                    if (options.onCancel) {
                        options.onCancel(choiceResult);
                    }
                    newSession.optedout = true;
                    console.log('User dismissed the A2HS prompt');
                }
                newSession.beforeInstallPrompt = null;
                updateSession(newSession);
            })
            .catch(function (err) {
                console.log(err.message);
                if (err.message.indexOf('The app is already installed') > -1) {
                    console.log(err.message);
                    session.added = true; // TODO: use this, together with optedout, to determine whether to show the update notification
                    updateSession({});
                } else {
                    console.log(err);
                    return err;
                }
            });
    };

    const setCanPromptToFalse = useCallback(
        (message, additionalUpdates = {}) => {
            console.log(message);
            if ((session.canPrompt) || !isEmpty(additionalUpdates)) {
                updateSession({ canPrompt: false, ...additionalUpdates });
            }
            return false;
        },
        [session, updateSession],
    );

    // perform various checks to see if we are cleared for prompting
    const canPrompt = () => {
        if (isEmpty(session)) {
            return false;
        }
        
        if (!passCustomCriteria()) {
            setCanPromptToFalse('Add to homescreen: not displaying callout because failed custom criteria');
            return false;
        }
        // the device is not supported
        if (!session.isCompatible) {
            setCanPromptToFalse('Add to homescreen: not displaying callout because device not supported');
            return false;
        }
        // critical errors:
        if (session.optedout) {
            setCanPromptToFalse('Add to homescreen: not displaying callout because user opted out');
            return false;
        }
        if (session.added) {
            setCanPromptToFalse('Add to homescreen: not displaying callout because already added to the homescreen');
            return false;
        }
        // check if the app is in stand alone mode
        // this applies to iOS
        if (session.isStandalone) {
            // execute the onAdd event if we haven't already
            if (!session.added) {
                if (options.onAdd) {
                    options.onAdd();
                }
                setCanPromptToFalse(
                    'Add to homescreen: not displaying callout because in standalone mode',
                    { added: true },
                );
                return false;
            }
            setCanPromptToFalse('Add to homescreen: not displaying callout because in standalone mode');
            return false;
        }

        if (!session.canPrompt) {
            updateSession({ canPrompt: true });
        }
        return true;
    };

    const notificationRef = useRef(null);
    const modalRef = useRef(null);
    return (
        <div ref={ notificationRef }>
            { canPrompt() && session.beforeInstallPrompt ? props.isNotification ?
                <div className='notification is-secondary mr-3 ml-3 mb-0' style={{ borderRadius: '10px' }}>
                    <button
                        className='delete'
                        onClick={ handleCancel }
                    ></button>
                    <div className='is-flex is-justify-content-center is-align-content-space-center mb2'>
                        <FontAwesomeIcon icon={ faArrowToBottom } className='pr-2 is-size-3'/><span className='is-size-5'>Install the app</span>
                    </div>
                    <div className='columns is-mobile'>
                        <div className='column is-third is-flex is-justify-content-space-around is-align-content-space-around'>
                            <div className='buttons mt-2'>
                                <a
                                    className='button is-info is-rounded'
                                    onClick={ handleInfo }
                                    style={{ width: '3.6rem', height: '3.6rem', fontSize: '1.25rem' }}
                                >
                                    <FontAwesomeIcon icon={faInfo} className='pt-1'/>
                                </a>
                            </div>
                        </div>
                        <div className='column is-two-thirds has-text-centred'>
                            <div className='columns is-mobile'>
                                <div className='column is-half is-flex is-justify-content-space-around is-align-content-space-around'>
                                    <div className='buttons mt-2'>
                                        <a  
                                            className='button is-danger is-rounded'
                                            onClick={ handleCancel }
                                            style={{ width: '3.6rem', height: '3.6rem', fontSize: '1.25rem' }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} className='pt-1'/>
                                        </a>
                                    </div>
                                </div>
                                <div className='column is-half is-flex is-justify-content-space-around is-align-content-space-around'>
                                    <div className='buttons mt-2'>
                                        <a  
                                            className='button is-success is-rounded'
                                            onClick={ (evt) => handleInstall(evt, 'Notifications') }
                                            style={{ width: '3.6rem', height: '3.6rem', fontSize: '1.25rem' }}
                                        >
                                            <FontAwesomeIcon icon={faCheck} className='pt-1'/>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> : <button
                    className='button is-primary is-rounded'
                    onClick={ (evt) => handleInstall(evt, 'Buttons') }
                >
                    <i className='fas fa-arrow-to-bottom pr-2'></i><strong>Install app</strong>
                </button> : ''}
            <div ref={ modalRef } className={ closedModalClass }>
                <div className='modal-background'></div>
                <div className='modal-card'>
                    <header className='modal-card-head'>
                        <LogoSmallIcon color='#dee5ed' className='pr-3' />
                        <p className='modal-card-title'>
                            <FontAwesomeIcon icon={faArrowToBottom} className='pr-2 pl-1 is-size-2'/>Install the app
                        </p>
                        <button
                            className='delete is-large'
                            aria-label='close'
                            onClick={ () => modalRef.current.className = closedModalClass }
                        ></button>
                    </header>
                    { modal.state === 'info' &&
                        <section className='modal-card-body'>
                            <div className='block'>You can install ChordFlow to your device.</div>
                            <div className='block'>The advantage of installing the app is that it removes the browser search bar at the top of the screen and navigation bar at the bottom of the screen, making the best use of the available space.</div>
                            <div className='block'>It also adds an icon to your device so you can launch it with a single click.</div>
                        </section>
                    }
                    { modal.state === 'mobileSafari' &&
                        <section className='modal-card-body'>
                            <div className='block'>Tap the <b>Share</b> button on the browser&#39;s toolbar — that&#39;s the square with an arrow pointing upward. It&#39;s on the bar at the top of the screen on an iPad, and in the centre of the bar at the bottom of the screen on an iPhone or iPod Touch. Tap the <b>Add to Home Screen</b> icon in the Share menu - that&#39;s a square with a <b>+</b> in it. On some iPhones you may have to swipe right and tap the <b>More</b> button (a square with 3 dots in it) to see this option.</div>
                            <div className='block'>You&#39;ll be prompted to name the shortcut before tapping the Add button. The shortcut can be dragged around and placed anywhere, including in app folders — just like a normal app icon.</div>
                        </section>
                    }
                    { modal.state === 'iosOther' &&
                        <section className='modal-card-body'>
                            <div className='block'>Unfortunately, you have to use Safari on iOS in order to add a website to the homescreen. Open this website in Safari and then follow these instructions:</div>
                            <div className='block'>Tap the <b>Share</b> button on the browser&#39;s toolbar — that&#39;s the square with an arrow pointing upward. It&#39;s on the bar at the top of the screen on an iPad, and in the centre of the bar at the bottom of the screen on an iPhone or iPod Touch. Tap the <b>Add to Home Screen</b> icon in the Share menu - that&#39;s a square with a <b>+</b> in it. On some iPhones you may have to swipe right and tap the <b>More</b> button (a square with 3 dots in it) to see this option.</div>
                            <div className='block'>You&#39;ll be prompted to name the shortcut before tapping the Add button. The shortcut can be dragged around and placed anywhere, including in app folders — just like a normal app icon.</div>
                        </section>
                    }
                    <footer className='modal-card-foot'>
                        <p>&copy; 2021 DogFish Music Ltd.</p>
                    </footer>
                </div>
            </div>
        </div>
    );
};

InstallPwaNotification.propTypes = {
    options: PropTypes.object,
    isNotification: PropTypes.bool,
    name: PropTypes.string,
};

InstallPwaNotification.defaultProps = {
    options: {
        onShow: () =>  {
            console.log('showing');
        },
        onInit: () => {
            console.log('initializing');
        },
        onAdd: () => {
            console.log('adding');
        },
        onInstall: () => {
            console.log('Installing');
        },
        onCancel: () => {
            console.log('Cancelling');
        },
    },
    isNotification: true,
    name: 'default',
};

export default React.memo(InstallPwaNotification);